/**
 * Lecture des refus premium renvoyés par l'API.
 *
 * Le SDK enveloppe les erreurs HTTP dans une `ApiException` et **perd le statut**
 * (cf. `login.component.ts`, qui lit `error.data.statusCode`). On ne peut donc
 * pas distinguer un 403 d'un 429 par le statut : c'est le `code` métier posé par
 * le back qui fait foi.
 *
 * Règle transverse : un refus premium ne produit JAMAIS un message d'erreur
 * rouge — toujours un encart, le paywall, ou une explication posée.
 */

export type PremiumErrorCode =
  | 'PREMIUM_REQUIRED'
  | 'TRIAL_ALREADY_USED'
  | 'ACCOUNT_REQUIRED'
  | 'NO_ACTIVE_SUBSCRIPTION'
  | 'PREMIUM_LIMIT_ZONES'
  | 'PREMIUM_LIMIT_BEDS'
  | 'PREMIUM_LOCKED_STRUCTURE'
  | 'WILLY_QUOTA_EXCEEDED'
  | 'WILLY_PHOTO_QUOTA_EXCEEDED'
  | 'WILLY_FAIR_USE';

/** Clé i18n du message à afficher pour chaque refus. */
const MESSAGE_BY_CODE: Partial<Record<PremiumErrorCode, string>> = {
  PREMIUM_LIMIT_ZONES: 'premium.gate.zone',
  PREMIUM_LIMIT_BEDS: 'premium.gate.bed',
  PREMIUM_LOCKED_STRUCTURE: 'premium.gate.locked-structure',
  WILLY_QUOTA_EXCEEDED: 'premium.willy.quota-free',
  WILLY_PHOTO_QUOTA_EXCEEDED: 'premium.willy.photo-quota',
  // Usage équitable : message neutre, JAMAIS présenté comme un quota et sans
  // aucun appel à l'achat — « illimité » a été promis.
  WILLY_FAIR_USE: 'premium.willy.fairuse',
  ACCOUNT_REQUIRED: 'premium.paywall.account-required',
};

/**
 * Refus pour lesquels proposer le paywall serait déplacé : l'utilisateur est
 * déjà abonné, ou l'achat ne réglerait pas son problème.
 */
const NO_PAYWALL: PremiumErrorCode[] = [
  'WILLY_FAIR_USE',
  'WILLY_PHOTO_QUOTA_EXCEEDED',
  'NO_ACTIVE_SUBSCRIPTION',
  'TRIAL_ALREADY_USED',
];

export interface PremiumRefusal {
  code: PremiumErrorCode;
  /** Clé i18n du message à afficher. */
  messageKey?: string;
  /** Faut-il ouvrir le paywall ? */
  shouldOpenPaywall: boolean;
  /** Quota concerné, quand le back le précise. */
  limit?: number;
}

/**
 * Reconnaît un refus premium dans une erreur d'API.
 * @returns `null` si l'erreur n'est pas liée au premium (à traiter normalement).
 */
export function readPremiumRefusal(error: unknown): PremiumRefusal | null {
  const data = (error as { data?: Record<string, unknown> })?.data;
  const code = (data?.['code'] ??
    (data?.['message'] as Record<string, unknown>)?.['code']) as
    PremiumErrorCode | undefined;

  if (!code || (!(code in MESSAGE_BY_CODE) && !isKnownCode(code))) return null;

  return {
    code,
    messageKey: MESSAGE_BY_CODE[code],
    shouldOpenPaywall: !NO_PAYWALL.includes(code),
    limit:
      typeof data?.['limit'] === 'number'
        ? (data['limit'] as number)
        : undefined,
  };
}

function isKnownCode(code: string): code is PremiumErrorCode {
  return [
    'PREMIUM_REQUIRED',
    'TRIAL_ALREADY_USED',
    'ACCOUNT_REQUIRED',
    'NO_ACTIVE_SUBSCRIPTION',
    'PREMIUM_LIMIT_ZONES',
    'PREMIUM_LIMIT_BEDS',
    'PREMIUM_LOCKED_STRUCTURE',
    'WILLY_QUOTA_EXCEEDED',
    'WILLY_PHOTO_QUOTA_EXCEEDED',
    'WILLY_FAIR_USE',
  ].includes(code);
}
